import React, { useEffect, useMemo, useState } from 'react';
import type { DashboardModel } from '../../hooks/useDashboard';
import type { TacticalEvent } from './types';
import { formatRelativeAirstripLocation } from '../../hooks/dashboard/dashboardUtils';
import { telemetryApi, type TelemetryEventsPayload } from '../../services/telemetryApi';

type CondensedEvent = TacticalEvent & {
  count?: number;
  durationMinutes?: number;
};

const speedBand = (speed: number) => {
  if (speed <= 2) return 'stopped';
  if (speed <= 25) return 'taxi';
  if (speed <= 80) return 'slow';
  if (speed <= 160) return 'cruise';
  return 'fast';
};

const altitudeBand = (altitude: number | null) => {
  if (altitude == null) return 'unknown';
  return String(Math.round(altitude / 1000) * 1000);
};

const locationGroupKey = (ev: TacticalEvent) => {
  if (typeof ev.lat === 'number' && typeof ev.lng === 'number') {
    return `${ev.lat.toFixed(2)},${ev.lng.toFixed(2)}`;
  }
  return ev.location || '--';
};

const toTime = (iso: string) => {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '--:--:--';
  return dt.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const isCoordText = (v: string) => /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(v);
const AIRBORNE_THRESHOLD_KT = 50;
const MOVING_THRESHOLD_KT = 3;

// Flight-phase events that should be merged into one Enroute segment
const FLIGHT_PHASE_ACTIONS = new Set(['Enroute', 'Approach', 'Slowing Down, Possible Approach']);
// Hard boundaries that always get their own row
const HARD_TRANSITION_ACTIONS = new Set(['Start Up', 'Take Off', 'Landing', 'Stopped Moving']);

/** Apply flight-phase and ground grouping to any array of CondensedEvent rows */
function applyGrouping(sorted: CondensedEvent[]): CondensedEvent[] {
  const grouped: CondensedEvent[] = [];
  for (const ev of sorted) {
    const last = grouped[grouped.length - 1];
    if (!last) { grouped.push({ ...ev, count: 1, durationMinutes: 0 }); continue; }

    const prevTs = new Date(last.timestamp).getTime();
    const currTs = new Date(ev.timestamp).getTime();
    const dtMin = Math.max(0, (currTs - prevTs) / 60000);

    const isHard = HARD_TRANSITION_ACTIONS.has(ev.action);
    const isFlight = FLIGHT_PHASE_ACTIONS.has(ev.action);
    const lastFlight = FLIGHT_PHASE_ACTIONS.has(last.action);

    // Merge consecutive flight-phase rows regardless of location/speed/alt drift
    if (!isHard && isFlight && lastFlight && dtMin < 12) {
      grouped[grouped.length - 1] = {
        ...ev,
        action: 'Enroute',
        location: last.location, // preserve origin label
        count: (last.count || 1) + 1,
        durationMinutes: (last.durationMinutes || 0) + dtMin,
      };
      continue;
    }

    // Merge same ground event at same location bucket, < 6 min gap
    const sameLoc = locationGroupKey(last) === locationGroupKey(ev);
    if (!isHard && !isFlight && last.action === ev.action && sameLoc && dtMin < 6) {
      grouped[grouped.length - 1] = {
        ...ev,
        count: (last.count || 1) + 1,
        durationMinutes: (last.durationMinutes || 0) + dtMin,
      };
      continue;
    }

    grouped.push({ ...ev, count: 1, durationMinutes: 0 });
  }
  return grouped;
}

const toHm = (value?: string | number | Date | null) => {
  if (!value) return '--';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '--';
  return dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const pickTime = (...values: Array<string | number | Date | null | undefined>) => {
  for (const v of values) {
    const formatted = toHm(v ?? null);
    if (formatted !== '--') return formatted;
  }
  return '--';
};

const isSameLocalDay = (value: string, dayRef: Date) => {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return false;
  return (
    dt.getFullYear() === dayRef.getFullYear() &&
    dt.getMonth() === dayRef.getMonth() &&
    dt.getDate() === dayRef.getDate()
  );
};

const formatDateHeader = (value: Date | null) => {
  if (!value || Number.isNaN(value.getTime())) return '--';
  return value.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const deriveEventLabel = (prev: TacticalEvent | null, curr: TacticalEvent, next: TacticalEvent | null) => {
  const speed = curr.speed || 0;
  const altitude = curr.altitude ?? null;
  const prevSpeed = prev?.speed || 0;
  const nextSpeed = next?.speed || 0;
  const prevAlt = prev?.altitude ?? altitude;

  if (prevSpeed <= 1 && speed > 3) return 'Start Up';
  if (prevSpeed > 5 && speed <= 2) return 'Stopped Moving';

  // Take Off: speed first crosses the airborne threshold — no altitude or prevSpeed check since
  // East African airstrips sit at 4,500–5,500 ft MSL (would fail old altitude gate) and the
  // Caravan accelerates through multiple readings (9kt→45kt→51kt) so prevSpeed can be >30 at crossing
  if (prevSpeed < AIRBORNE_THRESHOLD_KT && speed >= AIRBORNE_THRESHOLD_KT) return 'Take Off';

  // Landing: speed drops below airborne threshold while still moving
  if (prevSpeed >= AIRBORNE_THRESHOLD_KT && speed < AIRBORNE_THRESHOLD_KT && speed > MOVING_THRESHOLD_KT) return 'Landing';

  // Approach: fast and descending meaningfully
  if (altitude != null && prevAlt != null) {
    const altDelta = altitude - prevAlt;
    if (speed > 60 && altDelta <= -200) return 'Approach';
    if (speed > 80 && altDelta >= 300) return 'Enroute';
  }

  if (speed >= AIRBORNE_THRESHOLD_KT) return 'Enroute';
  if (speed > 5) return 'Taxiing';
  if (speed > 1) return 'Moving';
  if (nextSpeed > 3) return 'Stationary';
  return 'Stationary';
};

const DashboardEventsTab: React.FC<DashboardModel> = ({
  dark,
  selectedAircraft,
  selectedAircraftLocation,
  loadingHistory,
  telemetryHistory,
  aircraftFlightInfo,
  mapAirstrips,
}) => {
  const [backendEventsPayload, setBackendEventsPayload] = useState<TelemetryEventsPayload | null>(null);
  const [isLoadingBackendEvents, setIsLoadingBackendEvents] = useState(false);

  const latestActivityDate = useMemo(() => {
    if (selectedAircraft?.telemetry?.lastUpdate) {
      const dt = new Date(selectedAircraft.telemetry.lastUpdate);
      if (!Number.isNaN(dt.getTime())) return dt;
    }
    const latestHistoryTs = telemetryHistory
      .map((ev) => new Date(ev.timestamp).getTime())
      .filter((t) => Number.isFinite(t))
      .sort((a, b) => b - a)[0];
    if (Number.isFinite(latestHistoryTs)) return new Date(latestHistoryTs);
    return null;
  }, [selectedAircraft?.telemetry?.lastUpdate, telemetryHistory]);

  useEffect(() => {
    if (!selectedAircraft?.tailNumber || !latestActivityDate) {
      setBackendEventsPayload(null);
      return;
    }

    let mounted = true;
    const start = new Date(latestActivityDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(latestActivityDate);
    end.setHours(23, 59, 59, 999);

    setIsLoadingBackendEvents(true);
    telemetryApi
      .getEvents(selectedAircraft.tailNumber, start.toISOString(), end.toISOString(), 5000)
      .then((data) => {
        if (!mounted) return;
        if (data && Array.isArray(data.events) && data.events.length > 0) {
          setBackendEventsPayload(data);
        } else {
          setBackendEventsPayload(null);
        }
      })
      .catch(() => {
        if (mounted) setBackendEventsPayload(null);
      })
      .finally(() => {
        if (mounted) setIsLoadingBackendEvents(false);
      });

    return () => {
      mounted = false;
    };
  }, [selectedAircraft?.tailNumber, latestActivityDate]);

  const condensedEvents = useMemo(() => {
    if (!latestActivityDate) return [];
    const dayRef = latestActivityDate;

    const todayEvents = telemetryHistory
      .filter((ev) => {
        return isSameLocalDay(ev.timestamp, dayRef);
      })
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    const deduped: TacticalEvent[] = [];
    const seen = new Set<string>();
    for (const ev of todayEvents) {
      const key = [
        ev.timestamp,
        ev.lat ?? '--',
        ev.lng ?? '--',
        ev.altitude ?? '--',
        Number.isFinite(ev.speed) ? ev.speed.toFixed(2) : '--',
      ].join('|');
      if (!seen.has(key)) {
        deduped.push(ev);
        seen.add(key);
      }
    }

    const withLabels = deduped.map((curr, idx, arr) => {
      const prev = idx > 0 ? arr[idx - 1] : null;
      const next = idx < arr.length - 1 ? arr[idx + 1] : null;
      const label = deriveEventLabel(prev, curr, next);
      return { ...curr, action: label };
    });

    const grouped = applyGrouping(withLabels);

    // Deduplicate exact timestamp+action duplicates from telemetry noise
    const uniqueRows = new Map<string, CondensedEvent>();
    for (const ev of grouped) {
      const key = `${ev.timestamp}|${ev.action}`;
      if (!uniqueRows.has(key)) uniqueRows.set(key, ev);
    }

    return Array.from(uniqueRows.values()).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [telemetryHistory, latestActivityDate]);

  const backendCondensedEvents = useMemo<CondensedEvent[]>(() => {
    if (!backendEventsPayload?.events?.length) return [];
    const raw: CondensedEvent[] = backendEventsPayload.events.map((ev) => {
      const speed = Number(ev.groundSpeedKt || 0);
      const eventType = String(ev.eventType || '').toUpperCase();
      const type: 'GROUND' | 'FLIGHT' | 'ALERT' =
        eventType.includes('TAKEOFF') || eventType.includes('AIRBORNE') || speed >= AIRBORNE_THRESHOLD_KT
          ? 'FLIGHT'
          : 'GROUND';
      return {
        timestamp: ev.timestamp,
        action: ev.label || ev.eventType || 'Event',
        location: ev.locationLabel || ev.coordinates || '--',
        altitude: typeof ev.altitudeFt === 'number' ? ev.altitudeFt : null,
        speed,
        verticalSpeed: typeof ev.verticalSpeedFpm === 'number' ? ev.verticalSpeedFpm : null,
        heading: typeof ev.heading === 'number' ? ev.heading : undefined,
        type,
        lat: typeof ev.lat === 'number' ? ev.lat : undefined,
        lng: typeof ev.lng === 'number' ? ev.lng : undefined,
        coordKey:
          typeof ev.lat === 'number' && typeof ev.lng === 'number'
            ? `${ev.lat.toFixed(4)},${ev.lng.toFixed(4)}`
            : undefined,
      };
    });
    // Sort chronologically before grouping, then reverse to newest-first
    const sorted = [...raw].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    // Override labels for Take Off / Landing based on speed transitions — the backend API
    // often omits or mislabels these, especially at high-elevation East African airstrips
    const withPhases = sorted.map((ev, idx, arr) => {
      const prev = idx > 0 ? arr[idx - 1] : null;
      const prevSpeed = prev?.speed ?? 0;
      const speed = ev.speed ?? 0;
      if (prev && prevSpeed < AIRBORNE_THRESHOLD_KT && speed >= AIRBORNE_THRESHOLD_KT) {
        return { ...ev, action: 'Take Off' };
      }
      if (prev && prevSpeed >= AIRBORNE_THRESHOLD_KT && speed < AIRBORNE_THRESHOLD_KT && speed > MOVING_THRESHOLD_KT) {
        return { ...ev, action: 'Landing' };
      }
      return ev;
    });

    return applyGrouping(withPhases).reverse();
  }, [backendEventsPayload]);

  const activeEvents = backendCondensedEvents.length > 0 ? backendCondensedEvents : condensedEvents;

  const operationalSummary = useMemo(() => {
    const info = selectedAircraft ? aircraftFlightInfo[selectedAircraft.tailNumber] : undefined;
    const aircraftAny = selectedAircraft as any;

    const pilot = info?.pilotName || info?.pilot || aircraftAny?.pilotName || aircraftAny?.pilot || '--';
    const pilot2 =
      info?.coPilotName ||
      info?.coPilot ||
      aircraftAny?.coPilotName ||
      aircraftAny?.coPilot ||
      aircraftAny?.pilot2 ||
      '--';

    const dayRef = latestActivityDate || new Date();
    const rawDayEvents = telemetryHistory
      .filter((ev) => isSameLocalDay(ev.timestamp, dayRef))
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    type FlightCycle = {
      offBlock: TacticalEvent | null;
      takeoff: TacticalEvent | null;
      landing: TacticalEvent | null;
      onBlock: TacticalEvent | null;
      startAt: number;
      endAt: number;
      completed: boolean;
      endLocation: string;
    };

    const cycles: FlightCycle[] = [];
    let currentCycle: FlightCycle | null = null;
    let seenStationaryOrStartup = false;
    let wasAirborne = false;

    const startNewCycle = (offBlockEvent: TacticalEvent): FlightCycle => ({
      offBlock: offBlockEvent,
      takeoff: null,
      landing: null,
      onBlock: null,
      startAt: new Date(offBlockEvent.timestamp).getTime(),
      endAt: new Date(offBlockEvent.timestamp).getTime(),
      completed: false,
      endLocation: offBlockEvent.location || '--',
    });

    for (let i = 0; i < rawDayEvents.length; i++) {
      const curr = rawDayEvents[i];
      const prev = i > 0 ? rawDayEvents[i - 1] : null;
      const speed = Number(curr.speed || 0);
      const prevSpeed = Number(prev?.speed || 0);
      const action = curr.action;
      const currTs = new Date(curr.timestamp).getTime();

      const isStationaryLike = action === 'Stationary' || action === 'Start Up' || speed <= 1;
      const isMovingLike = action === 'Taxiing' || action === 'Moving' || speed > MOVING_THRESHOLD_KT;
      const isAirborne = action === 'Take Off' || action === 'Enroute' || speed >= AIRBORNE_THRESHOLD_KT;

      if (isStationaryLike) seenStationaryOrStartup = true;

      if (!currentCycle && isMovingLike && (seenStationaryOrStartup || prevSpeed <= MOVING_THRESHOLD_KT)) {
        currentCycle = startNewCycle(curr);
      }

      if (currentCycle) {
        currentCycle.endAt = currTs;
        currentCycle.endLocation = curr.location || currentCycle.endLocation;

        if (!currentCycle.takeoff && (action === 'Take Off' || (prevSpeed < AIRBORNE_THRESHOLD_KT && speed >= AIRBORNE_THRESHOLD_KT))) {
          currentCycle.takeoff = curr;
        }

        if (isAirborne) wasAirborne = true;

        if (!currentCycle.landing && wasAirborne) {
          const landedByAction = action === 'Landing' || action === 'Approach';
          const landedBySpeedDrop = prevSpeed >= AIRBORNE_THRESHOLD_KT && speed < AIRBORNE_THRESHOLD_KT;
          if (landedByAction || landedBySpeedDrop) {
            currentCycle.landing = curr;
          }
        }

        if (currentCycle.landing && !currentCycle.onBlock) {
          const onBlockByAction = action === 'Stopped Moving' || action === 'Stationary';
          const onBlockBySpeed = speed <= 1;
          if (onBlockByAction || onBlockBySpeed) {
            currentCycle.onBlock = curr;
            currentCycle.completed = true;
            cycles.push(currentCycle);
            currentCycle = null;
            seenStationaryOrStartup = true;
            wasAirborne = false;
          }
        }
      }
    }

    if (currentCycle) {
      cycles.push(currentCycle);
    }

    const sortedCycles = [...cycles].sort((a, b) => {
      const aKey = a.onBlock ? new Date(a.onBlock.timestamp).getTime() : (a.takeoff ? new Date(a.takeoff.timestamp).getTime() : a.endAt);
      const bKey = b.onBlock ? new Date(b.onBlock.timestamp).getTime() : (b.takeoff ? new Date(b.takeoff.timestamp).getTime() : b.endAt);
      return aKey - bKey;
    });

    const selectedLastUpdateTs = selectedAircraft?.telemetry?.lastUpdate
      ? new Date(selectedAircraft.telemetry.lastUpdate).getTime()
      : Number.NaN;
    const isCurrentlyAirborne = Number(selectedAircraft?.telemetry?.groundSpeed || 0) >= AIRBORNE_THRESHOLD_KT;

    const completedCycles = sortedCycles.filter((c) => c.completed && c.onBlock);
    const locationNeedle = (selectedAircraftLocation || info?.lastLocation || '').toLowerCase();
    const nearSelectedLocation = (cycle: FlightCycle) =>
      !!locationNeedle && !!cycle.endLocation && cycle.endLocation.toLowerCase().includes(locationNeedle);

    let selectedCycle: FlightCycle | null = null;

    if (isCurrentlyAirborne) {
      selectedCycle = [...sortedCycles].reverse().find((c) => !c.completed) || null;
    }

    if (!selectedCycle && Number.isFinite(selectedLastUpdateTs)) {
      const eligible = completedCycles.filter((c) => {
        const onBlockTs = c.onBlock ? new Date(c.onBlock.timestamp).getTime() : Number.NaN;
        return Number.isFinite(onBlockTs) && onBlockTs <= selectedLastUpdateTs;
      });
      if (eligible.length > 0) {
        const preferredByLocation = [...eligible].reverse().find(nearSelectedLocation);
        selectedCycle = preferredByLocation || eligible[eligible.length - 1];
      }
    }

    if (!selectedCycle && completedCycles.length > 0) {
      const preferredByLocation = [...completedCycles].reverse().find(nearSelectedLocation);
      selectedCycle = preferredByLocation || completedCycles[completedCycles.length - 1];
    }

    if (!selectedCycle && sortedCycles.length > 0) {
      selectedCycle = sortedCycles[sortedCycles.length - 1];
    }

    const offBlockEvent = selectedCycle?.offBlock || null;
    const takeoffEvent = selectedCycle?.takeoff || null;
    const landingEvent = selectedCycle?.landing || null;
    const onBlockEvent = selectedCycle?.onBlock || null;
    const backendCycle = backendEventsPayload?.selectedCycle || null;

    const missionLike =
      aircraftAny?.activeMission ||
      aircraftAny?.mission ||
      aircraftAny?.currentMission ||
      aircraftAny?.leg ||
      aircraftAny?.activeLeg ||
      aircraftAny?.flightInfo ||
      {};

    const missionOffBlock = pickTime(
      info?.offBlock,
      missionLike.offBlock,
      missionLike.offBlockTime,
      missionLike.offBlockAt,
      missionLike.departureOffBlockAt
    );
    const missionTakeoff = pickTime(
      info?.takeoff,
      missionLike.takeoff,
      missionLike.takeoffTime,
      missionLike.takeoffAt,
      missionLike.departureTakeoffAt
    );
    const missionLanding = pickTime(
      info?.landing,
      missionLike.landing,
      missionLike.landingTime,
      missionLike.landingAt,
      missionLike.arrivalLandingAt
    );
    const missionOnBlock = pickTime(
      info?.onBlock,
      missionLike.onBlock,
      missionLike.onBlockTime,
      missionLike.onBlockAt,
      missionLike.arrivalOnBlockAt
    );

    const hasMissionOperationalTimes =
      missionOffBlock !== '--' ||
      missionTakeoff !== '--' ||
      missionLanding !== '--' ||
      missionOnBlock !== '--';

    const offBlock = pickTime(
      backendCycle?.offBlock?.timestamp || null,
      hasMissionOperationalTimes ? missionOffBlock : null,
      offBlockEvent?.timestamp || null
    );
    const takeoff = pickTime(
      backendCycle?.takeoff?.timestamp || null,
      hasMissionOperationalTimes ? missionTakeoff : null,
      takeoffEvent?.timestamp || null
    );
    const landing = pickTime(
      backendCycle?.landing?.timestamp || null,
      hasMissionOperationalTimes ? missionLanding : null,
      landingEvent?.timestamp || null
    );
    const onBlock = pickTime(
      backendCycle?.onBlock?.timestamp || null,
      hasMissionOperationalTimes ? missionOnBlock : null,
      onBlockEvent?.timestamp || null
    );

    return { pilot, pilot2, offBlock, takeoff, landing, onBlock };
  }, [selectedAircraft, selectedAircraftLocation, aircraftFlightInfo, telemetryHistory, latestActivityDate, backendEventsPayload]);

  const selectedTelemetryLocation = useMemo(() => {
    const lat = selectedAircraft?.telemetry?.lat;
    const lng = selectedAircraft?.telemetry?.lng;
    return formatRelativeAirstripLocation(lat, lng, mapAirstrips);
  }, [selectedAircraft?.telemetry?.lat, selectedAircraft?.telemetry?.lng, mapAirstrips]);

  const milestones = [
    { label: 'Off Block', time: operationalSummary.offBlock, textColor: dark ? 'text-slate-300' : 'text-slate-700',    dot: dark ? 'bg-slate-500' : 'bg-slate-400' },
    { label: 'Takeoff',   time: operationalSummary.takeoff,  textColor: dark ? 'text-emerald-300' : 'text-emerald-600', dot: dark ? 'bg-emerald-400' : 'bg-emerald-500' },
    { label: 'Landing',   time: operationalSummary.landing,  textColor: dark ? 'text-amber-300' : 'text-amber-600',    dot: dark ? 'bg-amber-400' : 'bg-amber-500' },
    { label: 'On Block',  time: operationalSummary.onBlock,  textColor: dark ? 'text-slate-300' : 'text-slate-700',    dot: dark ? 'bg-slate-500' : 'bg-slate-400' },
  ];

  const tel = selectedAircraft?.telemetry;

  return (
    <div className={`flex-1 flex flex-col overflow-hidden ${dark ? 'bg-slate-900/60' : 'bg-[#f8f9fb]'}`}>

      {/* ── Pilot + milestone timeline ── */}
      <div className={`px-4 pt-3 pb-3 border-b ${dark ? 'bg-slate-900/80 border-slate-700/60' : 'bg-white border-slate-100'}`}>
        {/* Pilot chips */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {operationalSummary.pilot && operationalSummary.pilot !== '--' && (
            <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 border ${dark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <div className="w-5 h-5 rounded-full bg-indigo-600 text-white text-[8px] font-black flex items-center justify-center shrink-0 uppercase">
                {operationalSummary.pilot.charAt(0)}
              </div>
              <span className={`text-[10px] font-semibold ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{operationalSummary.pilot}</span>
              <span className={`text-[7px] font-black uppercase tracking-[0.12em] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>PIC</span>
            </div>
          )}
          {operationalSummary.pilot2 && operationalSummary.pilot2 !== '--' && (
            <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 border ${dark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <div className="w-5 h-5 rounded-full bg-violet-600 text-white text-[8px] font-black flex items-center justify-center shrink-0 uppercase">
                {operationalSummary.pilot2.charAt(0)}
              </div>
              <span className={`text-[10px] font-semibold ${dark ? 'text-slate-200' : 'text-slate-700'}`}>{operationalSummary.pilot2}</span>
              <span className={`text-[7px] font-black uppercase tracking-[0.12em] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>FO</span>
            </div>
          )}
        </div>

        {/* Milestone timeline */}
        <div className="flex items-start">
          {milestones.map((m, idx, arr) => {
            const done = m.time !== '--';
            const prevDone = idx > 0 && arr[idx - 1].time !== '--';
            return (
              <React.Fragment key={m.label}>
                <div className="flex flex-col items-center" style={{ flex: 1 }}>
                  <div className={`w-2 h-2 rounded-full mb-1.5 ring-2 ${
                    done
                      ? `${m.dot} ring-white/40`
                      : dark ? 'bg-slate-800 ring-slate-700' : 'bg-slate-100 ring-slate-300'
                  }`} />
                  <span className={`text-[9px] font-black font-mono tabular-nums leading-none ${
                    done ? m.textColor : dark ? 'text-slate-600' : 'text-slate-300'
                  }`}>{m.time}</span>
                  <span className={`text-[6px] font-bold uppercase tracking-[0.1em] mt-1 ${dark ? 'text-slate-600' : 'text-slate-400'}`}>{m.label}</span>
                </div>
                {idx < arr.length - 1 && (
                  <div className={`h-px mt-[4px] flex-1 mx-0.5 transition-colors ${
                    done && prevDone
                      ? 'bg-indigo-300/70'
                      : dark ? 'bg-slate-700' : 'bg-slate-200'
                  }`} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Live telemetry ── */}
      {tel ? (
        <div className={`px-4 py-3 border-b ${dark ? 'bg-slate-900/60 border-slate-700/50' : 'bg-white border-slate-100'}`}>
          {/* Location chip */}
          {(selectedTelemetryLocation?.primary || selectedAircraftLocation) && (
            <div className="flex items-center gap-1.5 mb-2.5">
              <svg className={`w-3 h-3 shrink-0 ${dark ? 'text-indigo-400' : 'text-indigo-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <span className={`text-[10px] font-semibold ${dark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                {selectedTelemetryLocation?.primary || selectedAircraftLocation}
              </span>
              <span className={`text-[8px] font-mono ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                {tel.lat.toFixed(4)}, {tel.lng.toFixed(4)}
              </span>
            </div>
          )}

          {/* 3 stat chips */}
          <div className="grid grid-cols-3 gap-2">
            {[
              {
                label: 'Altitude',
                raw: tel.altitude > 0 ? Math.round(tel.altitude).toLocaleString() : null,
                unit: 'ft',
                valueColor: dark ? 'text-blue-300' : 'text-blue-700',
                bg: dark ? 'bg-blue-950/50 border-blue-800/30' : 'bg-blue-50 border-blue-100',
              },
              {
                label: 'Gnd Speed',
                raw: tel.groundSpeed > 0 ? String(Math.round(tel.groundSpeed)) : null,
                unit: 'kt',
                valueColor: dark ? 'text-emerald-300' : 'text-emerald-700',
                bg: dark ? 'bg-emerald-950/50 border-emerald-800/30' : 'bg-emerald-50 border-emerald-100',
              },
              {
                label: 'Heading',
                raw: tel.heading > 0 ? String(Math.round(tel.heading)) : null,
                unit: '°',
                valueColor: dark ? 'text-violet-300' : 'text-violet-700',
                bg: dark ? 'bg-violet-950/50 border-violet-800/30' : 'bg-violet-50 border-violet-100',
              },
            ].map((stat) => {
              const hasData = stat.raw !== null;
              return (
                <div
                  key={stat.label}
                  className={`rounded-xl px-2.5 py-2 border transition-colors ${
                    hasData ? stat.bg : dark ? 'bg-slate-800/40 border-slate-700/40' : 'bg-slate-50/80 border-slate-200/60'
                  }`}
                >
                  {hasData ? (
                    <div className={`text-[15px] font-black leading-none ${stat.valueColor}`}>
                      {stat.raw}
                      <span className="text-[8px] font-semibold ml-0.5 opacity-60">{stat.unit}</span>
                    </div>
                  ) : (
                    <div className={`text-[13px] font-black leading-none ${dark ? 'text-slate-700' : 'text-slate-300'}`}>—</div>
                  )}
                  <div className={`text-[7px] font-bold uppercase tracking-[0.1em] mt-1 ${
                    hasData ? (dark ? 'text-slate-500' : 'text-slate-400') : (dark ? 'text-slate-700' : 'text-slate-300')
                  }`}>
                    {stat.label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Updated timestamp */}
          {tel.lastUpdate && (
            <p className={`text-right text-[7px] font-medium mt-1.5 ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
              Updated {new Date(tel.lastUpdate).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </p>
          )}
        </div>
      ) : (
        <div className={`px-4 py-3 border-b ${dark ? 'bg-slate-900/60 border-slate-700/50' : 'bg-white border-slate-100'}`}>
          <p className={`text-[9px] font-semibold ${dark ? 'text-slate-500' : 'text-slate-400'}`}>No live telemetry</p>
        </div>
      )}

      {/* ── Date divider ── */}
      <div className={`px-4 py-2 flex items-center gap-3 ${dark ? 'bg-slate-900/40' : 'bg-[#f8f9fb]'}`}>
        <div className={`h-px flex-1 ${dark ? 'bg-slate-700/60' : 'bg-slate-200'}`} />
        <span className={`text-[7px] font-black uppercase tracking-[0.15em] px-2.5 py-1 rounded-full ${
          dark ? 'bg-slate-800 text-slate-400 border border-slate-700' : 'bg-white text-slate-500 border border-slate-200 shadow-sm'
        }`}>
          {formatDateHeader(latestActivityDate)}
        </span>
        <div className={`h-px flex-1 ${dark ? 'bg-slate-700/60' : 'bg-slate-200'}`} />
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <table className="w-full text-left table-fixed">
          <colgroup>
            <col style={{ width: '72px' }} />
            <col style={{ width: '44%' }} />
            <col />
          </colgroup>
          <thead className={`sticky top-0 z-10 border-b ${dark ? 'bg-slate-900 border-slate-700/60' : 'bg-white border-slate-200'}`}>
            <tr>
              <th className={`px-3 py-2.5 text-[7px] font-black uppercase tracking-[0.12em] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Time</th>
              <th className={`px-3 py-2.5 text-[7px] font-black uppercase tracking-[0.12em] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Event</th>
              <th className={`px-3 py-2.5 text-[7px] font-black uppercase tracking-[0.12em] ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Location</th>
            </tr>
          </thead>
          <tbody>
            {(loadingHistory || isLoadingBackendEvents) && activeEvents.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <div className={`w-7 h-7 rounded-full border-2 border-t-transparent animate-spin ${dark ? 'border-indigo-400' : 'border-indigo-500'}`} />
                    <p className={`text-[9px] font-bold uppercase tracking-widest ${dark ? 'text-slate-500' : 'text-slate-400'}`}>Loading history…</p>
                  </div>
                </td>
              </tr>
            ) : activeEvents.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-4 py-12 text-center">
                  <p className={`text-[9px] font-semibold ${dark ? 'text-slate-600' : 'text-slate-400'}`}>
                    {selectedAircraft?.telemetry || telemetryHistory.length > 0 ? 'No history for this window' : 'No telemetry available'}
                  </p>
                </td>
              </tr>
            ) : (
              activeEvents.map((ev, i) => {
                const isFlight = ev.type === 'FLIGHT' || ev.action === 'Enroute' || ev.action === 'Approach';
                const isAlert = ev.type === 'ALERT';
                const isStationary = ev.action === 'Stationary' || ev.action === 'Stopped Moving';
                const isKeyEvent = ['Take Off', 'Landing', 'Start Up'].includes(ev.action);
                const isMergedFlight = ev.action === 'Enroute' && (ev.count || 1) > 1;

                const badgeClass = (() => {
                  if (ev.action === 'Take Off')   return dark ? 'bg-emerald-500/20 text-emerald-300 font-black' : 'bg-emerald-100 text-emerald-700 font-black';
                  if (ev.action === 'Landing')    return dark ? 'bg-amber-500/20 text-amber-300 font-black' : 'bg-amber-100 text-amber-700 font-black';
                  if (ev.action === 'Approach')   return dark ? 'bg-amber-500/12 text-amber-400' : 'bg-amber-50 text-amber-600';
                  if (isFlight)                   return dark ? 'bg-indigo-500/12 text-indigo-300' : 'bg-indigo-50 text-indigo-600';
                  if (isAlert)                    return dark ? 'bg-rose-500/15 text-rose-300' : 'bg-rose-50 text-rose-600';
                  if (isStationary)               return dark ? 'bg-slate-700/40 text-slate-500' : 'bg-slate-100 text-slate-400';
                  return dark ? 'bg-sky-500/12 text-sky-300' : 'bg-sky-50 text-sky-600';
                })();

                const altText = ev.altitude && ev.altitude > 0 ? `${Math.round(ev.altitude).toLocaleString()}` : null;
                const vsText  = typeof ev.verticalSpeed === 'number' && Number.isFinite(ev.verticalSpeed) ? `${Number(ev.verticalSpeed).toFixed(0)}` : null;
                const gsText  = ev.speed && ev.speed > 0 ? `${Math.round(ev.speed)}` : null;

                return (
                  <tr
                    key={`${ev.timestamp}-${ev.action}-${ev.location}-${i}`}
                    className={`transition-colors border-b ${
                      dark ? 'border-slate-800/60' : 'border-slate-100'
                    } ${
                      isStationary ? 'opacity-50' : ''
                    } ${
                      isFlight
                        ? dark ? 'hover:bg-indigo-950/30' : 'hover:bg-indigo-50/60'
                        : dark ? 'hover:bg-slate-800/40' : 'hover:bg-slate-50'
                    }`}
                  >
                    {/* TIME */}
                    <td className={`px-3 py-2.5 align-top ${isKeyEvent ? `border-l-2 ${
                      ev.action === 'Take Off' ? 'border-emerald-500' :
                      ev.action === 'Landing'  ? 'border-amber-500' :
                      'border-sky-400'
                    }` : 'border-l-2 border-transparent'}`}>
                      <span className={`font-mono text-[9px] font-semibold tabular-nums whitespace-nowrap ${
                        isStationary ? (dark ? 'text-slate-600' : 'text-slate-400') : (dark ? 'text-slate-300' : 'text-slate-600')
                      }`}>{toTime(ev.timestamp)}</span>
                    </td>

                    {/* EVENT badge + meta */}
                    <td className="px-3 py-2.5 align-top">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex items-center w-fit px-2 py-0.5 rounded-full text-[7px] font-black uppercase tracking-[0.08em] ${badgeClass}`}>
                          {ev.action}
                        </span>
                        {isMergedFlight && ev.durationMinutes && ev.durationMinutes > 0 ? (
                          <span className={`text-[8px] font-bold ${dark ? 'text-indigo-400' : 'text-indigo-500'}`}>
                            {Math.round(ev.durationMinutes)} min
                          </span>
                        ) : (ev.count || 1) > 1 ? (
                          <span className={`text-[8px] font-semibold ${dark ? 'text-slate-500' : 'text-slate-400'}`}>
                            ×{ev.count}{ev.durationMinutes && ev.durationMinutes > 0 ? ` · ${Math.round(ev.durationMinutes)}m` : ''}
                          </span>
                        ) : null}
                        {/* Colored data chips */}
                        {(altText || vsText || gsText) && (
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {altText && (
                              <span className={`font-mono text-[8px] font-semibold ${isStationary ? (dark ? 'text-blue-800' : 'text-blue-300') : (dark ? 'text-blue-400' : 'text-blue-600')}`}>
                                {altText}<span className="opacity-60 ml-px">ft</span>
                              </span>
                            )}
                            {vsText && (
                              <span className={`font-mono text-[8px] font-semibold ${isStationary ? (dark ? 'text-violet-800' : 'text-violet-300') : (dark ? 'text-violet-400' : 'text-violet-600')}`}>
                                {vsText}<span className="opacity-60 ml-px">fpm</span>
                              </span>
                            )}
                            {gsText && (
                              <span className={`font-mono text-[8px] font-semibold ${isStationary ? (dark ? 'text-emerald-800' : 'text-emerald-300') : (dark ? 'text-emerald-400' : 'text-emerald-600')}`}>
                                {gsText}<span className="opacity-60 ml-px">kt</span>
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* LOCATION */}
                    <td className="px-3 py-2.5 align-top">
                      <div className="flex flex-col gap-0.5">
                        {(() => {
                          const relative = formatRelativeAirstripLocation(ev.lat, ev.lng, mapAirstrips);
                          const primary =
                            ev.location && !isCoordText(ev.location)
                              ? ev.location
                              : (relative?.primary || `${ev.lat?.toFixed(4)}, ${ev.lng?.toFixed(4)}`);
                          const secondary = relative?.secondary || `${ev.lat?.toFixed(4)}, ${ev.lng?.toFixed(4)}`;
                          return (
                            <>
                              <span className={`text-[9px] font-semibold leading-tight ${dark ? 'text-slate-200' : 'text-slate-800'}`}>{primary}</span>
                              <span className={`text-[8px] font-mono ${dark ? 'text-slate-600' : 'text-slate-400'}`}>{secondary}</span>
                            </>
                          );
                        })()}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DashboardEventsTab;
